// Simple PDF.js viewer - CSP compliant
class PDFViewer {
    constructor(container, pdfUrl, options = {}) {
        this.container = container;
        this.pdfUrl = pdfUrl;
        const { scale, minScale, maxScale, ...restOptions } = options;

        this.options = {
            ...restOptions,
            minScale: typeof minScale === 'number' ? minScale : 0.5,
            maxScale: typeof maxScale === 'number' ? maxScale : 5.0
        };

        this.currentScale = typeof scale === 'number' && !Number.isNaN(scale)
            ? scale
            : null;
        this.autoFit = this.currentScale === null;
        this.resizeTimeout = null;
        this.handleResize = this.handleResize.bind(this);

        this.pdf = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.isRendering = false;

        this.init();
    }

    async init() {
        try {
            // Wait for PDF.js to be ready
            if (!window.pdfjsLib) {
                setTimeout(() => this.init(), 100);
                return;
            }

            this.createUI();
            await this.loadPDF();
        } catch (error) {
            console.error('PDF Viewer initialization failed:', error);
            this.showError('無法初始化 PDF 檢視器');
        }
    }

    createUI() {
        this.container.innerHTML = `
            <div class="pdf-viewer">
                <div class="pdf-toolbar">
                    <div class="pdf-controls">
                        <button class="pdf-btn pdf-prev" title="上一頁">‹</button>
                        <span class="pdf-page-info">
                            <input type="number" class="pdf-page-input" min="1" value="1">
                            / <span class="pdf-total-pages">0</span>
                        </span>
                        <button class="pdf-btn pdf-next" title="下一頁">›</button>
                    </div>
                    <div class="pdf-actions">
                        <button class="pdf-btn pdf-zoom-out" title="縮小">-</button>
                        <span class="pdf-zoom-level">120%</span>
                        <button class="pdf-btn pdf-zoom-in" title="放大">+</button>
                    </div>
                </div>
                <div class="pdf-content">
                    <div class="pdf-loading">載入中...</div>
                    <canvas class="pdf-canvas"></canvas>
                </div>
            </div>
        `;

        this.bindEvents();
    }

    bindEvents() {
        const container = this.container;

        // Navigation
        container.querySelector('.pdf-prev').addEventListener('click', () => this.prevPage());
        container.querySelector('.pdf-next').addEventListener('click', () => this.nextPage());

        // Page input
        const pageInput = container.querySelector('.pdf-page-input');
        pageInput.addEventListener('change', (e) => {
            const page = parseInt(e.target.value);
            if (page >= 1 && page <= this.totalPages) {
                this.goToPage(page);
            } else {
                e.target.value = this.currentPage;
            }
        });

        // Zoom
        container.querySelector('.pdf-zoom-in').addEventListener('click', () => this.zoomIn());
        container.querySelector('.pdf-zoom-out').addEventListener('click', () => this.zoomOut());

        // Actions removed for simplicity

        // Keyboard navigation (single page mode)
        document.addEventListener('keydown', (e) => {
            if (this.container.querySelector('.pdf-canvas:hover')) {
                if (e.key === 'ArrowLeft') this.prevPage();
                if (e.key === 'ArrowRight') this.nextPage();
                if (e.key === 'Home') this.goToPage(1);
                if (e.key === 'End') this.goToPage(this.totalPages);
            }
        });

        window.addEventListener('resize', this.handleResize);
    }

    async loadPDF() {
        try {
            this.showLoading(true);

            this.pdf = await window.pdfjsLib.getDocument(this.pdfUrl).promise;
            this.totalPages = this.pdf.numPages;

            this.container.querySelector('.pdf-total-pages').textContent = this.totalPages;
            this.container.querySelector('.pdf-page-input').max = this.totalPages;

            await this.renderPage(1);
            this.showLoading(false);

        } catch (error) {
            console.error('PDF loading failed:', error);
            this.showError('無法載入 PDF 文件');
        }
    }

    async renderPage(pageNum) {
        if (this.isRendering) return;

        try {
            this.isRendering = true;
            this.currentPage = pageNum;

            const page = await this.pdf.getPage(pageNum);

            if (this.autoFit && !this.currentScale) {
                this.currentScale = this.calculateFitScale(page);
            } else if (!this.currentScale) {
                this.currentScale = 1.0;
            }

            const viewport = page.getViewport({ scale: this.currentScale });

            const canvas = this.container.querySelector('.pdf-canvas');
            const context = canvas.getContext('2d');

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            await page.render(renderContext).promise;

            // Update UI
            this.container.querySelector('.pdf-page-input').value = pageNum;
            this.updateNavigationButtons();
            this.updateZoomUI();

        } catch (error) {
            console.error('Page rendering failed:', error);
            this.showError(`無法渲染第 ${pageNum} 頁`);
        } finally {
            this.isRendering = false;
        }
    }

    updateNavigationButtons() {
        const prevBtn = this.container.querySelector('.pdf-prev');
        const nextBtn = this.container.querySelector('.pdf-next');

        prevBtn.disabled = this.currentPage <= 1;
        nextBtn.disabled = this.currentPage >= this.totalPages;
    }

    async goToPage(pageNum) {
        if (pageNum >= 1 && pageNum <= this.totalPages && pageNum !== this.currentPage) {
            await this.renderPage(pageNum);
        }
    }

    async prevPage() {
        if (this.currentPage > 1) {
            await this.renderPage(this.currentPage - 1);
        }
    }

    async nextPage() {
        if (this.currentPage < this.totalPages) {
            await this.renderPage(this.currentPage + 1);
        }
    }

    zoomIn() {
        if (!this.currentScale || this.isRendering) return;
        this.autoFit = false;
        this.currentScale = Math.min(this.currentScale * 1.2, this.options.maxScale);
        this.updateZoomUI();
        this.renderPage(this.currentPage);
    }

    zoomOut() {
        if (!this.currentScale || this.isRendering) return;
        this.autoFit = false;
        this.currentScale = Math.max(this.currentScale / 1.2, this.options.minScale);
        this.updateZoomUI();
        this.renderPage(this.currentPage);
    }

    updateZoomUI() {
        if (!this.currentScale) return;
        const zoomLevel = Math.round(this.currentScale * 100);
        this.container.querySelector('.pdf-zoom-level').textContent = `${zoomLevel}%`;
    }

    // Download and print methods removed for simplicity

    showLoading(show) {
        const loading = this.container.querySelector('.pdf-loading');
        const canvas = this.container.querySelector('.pdf-canvas');

        if (show) {
            loading.style.display = 'block';
            canvas.style.display = 'none';
        } else {
            loading.style.display = 'none';
            canvas.style.display = 'block';
        }
    }

    showError(message) {
        this.container.innerHTML = `
            <div class="pdf-error">
                <p>${message}</p>
                <button onclick="location.reload()">重新載入</button>
            </div>
        `;
        window.removeEventListener('resize', this.handleResize);
    }

    calculateFitScale(page) {
        const content = this.container.querySelector('.pdf-content');
        if (!content) return this.currentScale || 1.0;

        const styles = window.getComputedStyle(content);
        const paddingLeft = parseFloat(styles.paddingLeft) || 0;
        const paddingRight = parseFloat(styles.paddingRight) || 0;
        const availableWidth = content.clientWidth - paddingLeft - paddingRight;

        if (availableWidth <= 0) {
            return this.currentScale || 1.0;
        }

        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = availableWidth / unscaledViewport.width;

        return scale > 0 ? scale : (this.currentScale || 1.0);
    }

    handleResize() {
        if (!this.autoFit || !this.pdf) {
            return;
        }

        if (this.isRendering) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(this.handleResize, 150);
            return;
        }

        this.currentScale = null;
        this.renderPage(this.currentPage);
    }
}

// Auto-initialize PDF viewers
document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-pdf-viewer]').forEach(element => {
        const pdfUrl = element.getAttribute('data-pdf-url');
        if (pdfUrl) {
            const options = {};
            const scaleAttr = element.getAttribute('data-scale');
            if (scaleAttr !== null) {
                const parsedScale = parseFloat(scaleAttr);
                if (!Number.isNaN(parsedScale)) {
                    options.scale = parsedScale;
                }
            }

            new PDFViewer(element, pdfUrl, options);
        }
    });
});

// Export for manual use
window.PDFViewer = PDFViewer;
